import {AuthModel} from './_models'

export interface AuthSessionEvent {
  type: 'login'
  userId: string
  eventId: string
}

const AUTH_SESSION_CHANNEL_NAME = 'office-sia-auth-session'
const AUTH_SESSION_STORAGE_KEY = 'office-sia-auth-session-event'
let authSessionChannel: BroadcastChannel | undefined

const getAuthSessionChannel = () => {
  if (typeof BroadcastChannel === 'undefined') return undefined
  authSessionChannel ??= new BroadcastChannel(AUTH_SESSION_CHANNEL_NAME)
  return authSessionChannel
}

export function announceAuthSessionLogin(userId: string) {
  if (!userId || typeof window === 'undefined') return

  const event: AuthSessionEvent = {
    type: 'login',
    userId,
    eventId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  }
  const channel = getAuthSessionChannel()
  if (channel) {
    channel.postMessage(event)
    return
  }

  try {
    localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(event))
    localStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
  } catch (error) {
    // The server still enforces the session policy if browser storage is unavailable.
  }
}

export function subscribeToAuthSessionLogin(onLogin: (event: AuthSessionEvent) => void) {
  if (typeof window === 'undefined') return () => {}

  const channel = getAuthSessionChannel()
  if (channel) {
    const handleMessage = (message: MessageEvent<AuthSessionEvent>) => {
      if (message.data?.type === 'login' && message.data.userId) onLogin(message.data)
    }
    channel.addEventListener('message', handleMessage)
    return () => channel.removeEventListener('message', handleMessage)
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== AUTH_SESSION_STORAGE_KEY || !event.newValue) return
    try {
      const message = JSON.parse(event.newValue) as AuthSessionEvent
      if (message.type === 'login' && message.userId) onLogin(message)
    } catch (error) {
      // Ignore malformed cross-tab messages.
    }
  }
  window.addEventListener('storage', handleStorage)
  return () => window.removeEventListener('storage', handleStorage)
}

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const getAuth = (): AuthModel | undefined => {
  if (!sessionStorage) {
    return
  }

  const lsValue: string | null = sessionStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}

const setAuth = (auth: AuthModel) => {
  if (!sessionStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    sessionStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (!sessionStorage) {
    return
  }

  try {
    sessionStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

export function setupAxios() {
  let token = ''
  let tenantId = ''
  let refreshToken = ''
  let id = ''

  const auth = getAuth()

  if (auth && auth.token) {
    token = auth.token
  }
  if (auth && auth.refreshToken) {
    refreshToken = auth.refreshToken
  }
  if (auth && auth.id) {
    id = auth.id
  }
  if (auth && auth.tenantId) {
    tenantId = auth.tenantId
  }
  return {token, refreshToken, id, tenantId}
}

export {getAuth, setAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY}
